/**
 * @fileoverview Provide shared presentation vocabulary within administration screens. Pages, sections, cards, and status notes frame content whose loading and actions belong to the consuming workspace. This layer carries no business state or API authority; general controls live in `kc-internal-frontend/src/components/shared/index.js`.
 */

import React from 'react'

const pageStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '0.25rem',
    paddingBottom: '1.5rem',
}

const sectionStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
}

const headerStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
    padding: '1rem 1.25rem',
    borderRadius: 'var(--admin-radius-lg)',
    border: '1px solid var(--kc-surface-edge)',
    backgroundColor: 'var(--kc-surface-region)',
    boxShadow: 'none',
}

const titleWrapStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    minWidth: '18rem',
    flex: '1 1 28rem',
}

const sectionTitleWrapStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    minWidth: '16rem',
    flex: '1 1 24rem',
}

const sectionHeaderStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '1rem',
}

const actionWrapStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: '0 1 auto',
}

const cardStyle = {
    border: '1px solid var(--kc-surface-edge)',
    borderRadius: 'var(--admin-radius-lg)',
    backgroundColor: 'var(--kc-surface-section)',
    boxShadow: 'none',
    overflow: 'hidden',
}

const cardHeaderStyle = {
    padding: '1rem 1.25rem',
    borderBottom: '1px solid var(--kc-surface-edge)',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.75rem',
}

const cardBodyStyle = {
    padding: '1rem 1.25rem',
}

const noteToneStyles = {
    neutral: {
        backgroundColor: 'var(--kc-surface-inset)',
        borderColor: 'var(--kc-surface-edge)',
        color: 'var(--admin-note-neutral-text)',
    },
    info: {
        backgroundColor: 'var(--admin-note-info-bg)',
        borderColor: 'var(--admin-note-info-border)',
        color: 'var(--admin-note-info-text)',
    },
    warning: {
        backgroundColor: 'var(--admin-note-warning-bg)',
        borderColor: 'var(--admin-note-warning-border)',
        color: 'var(--admin-note-warning-text)',
    },
    success: {
        backgroundColor: 'var(--admin-note-success-bg)',
        borderColor: 'var(--admin-note-success-border)',
        color: 'var(--admin-note-success-text)',
    },
}



export function AdminPage({ title, description, actions, children }) {
    return (
        <div style={pageStyle}>
            <div style={headerStyle}>
                <div style={titleWrapStyle}>
                    <div style={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-color-secondary)' }}>
                        Admin Workspace
                    </div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-color)' }}>{title}</div>
                    {description && (
                        <div style={{ maxWidth: '64rem', fontSize: '0.95rem', lineHeight: 1.7, color: 'var(--text-color-secondary)' }}>
                            {description}
                        </div>
                    )}
                </div>
                {actions ? <div style={actionWrapStyle}>{actions}</div> : null}
            </div>
            {children}
        </div>
    )
}



export function AdminSection({ title, description, actions, children, eyebrow }) {
    return (
        <div style={sectionStyle}>
            {(title || description || actions || eyebrow) && (
                <div style={sectionHeaderStyle}>
                    <div style={sectionTitleWrapStyle}>
                        {eyebrow ? (
                            <div style={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.76rem', fontWeight: 600, color: 'var(--text-color-secondary)' }}>
                                {eyebrow}
                            </div>
                        ) : null}
                        {title ? <div style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-color)' }}>{title}</div> : null}
                        {description ? (
                            <div style={{ maxWidth: '64rem', fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--text-color-secondary)' }}>
                                {description}
                            </div>
                        ) : null}
                    </div>
                    {actions ? <div style={actionWrapStyle}>{actions}</div> : null}
                </div>
            )}
            {children}
        </div>
    )
}



export function AdminCard({ title, description, actions, children, padded = true }) {
    return (
        <div style={cardStyle}>
            {(title || description || actions) && (
                <div style={cardHeaderStyle}>
                    <div style={{ minWidth: '16rem', flex: '1 1 24rem' }}>
                        {title ? <div style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-color)' }}>{title}</div> : null}
                        {description ? <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--text-color-secondary)' }}>{description}</div> : null}
                    </div>
                    {actions ? <div style={actionWrapStyle}>{actions}</div> : null}
                </div>
            )}
            <div style={padded ? cardBodyStyle : undefined}>
                {children}
            </div>
        </div>
    )
}



export function AdminNote({ tone = 'neutral', children, title }) {
    const toneStyle = noteToneStyles[tone] || noteToneStyles.neutral
    return (
        <div
            style={{
                borderRadius: 'var(--admin-radius-sm)',
                border: `1px solid ${toneStyle.borderColor}`,
                backgroundColor: toneStyle.backgroundColor,
                color: toneStyle.color,
                padding: '0.9rem 1rem',
            }}
        >
            {title ? <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{title}</div> : null}
            <div style={{ fontSize: '0.9rem', lineHeight: 1.7 }}>{children}</div>
        </div>
    )
}



export function AdminEmptyState({ title, description }) {
    return (
        <div
            style={{
                padding: '2rem 1rem',
                textAlign: 'center',
                border: '1px dashed var(--kc-surface-edge-strong)',
                borderRadius: 'var(--admin-radius-sm)',
                backgroundColor: 'var(--kc-surface-inset)',
            }}
        >
            <div style={{ fontSize: '1.125rem', fontWeight: 500, color: 'var(--text-color)' }}>{title}</div>
            {description ? <div style={{ fontSize: '0.9rem', color: 'var(--text-color-secondary)', marginTop: '0.5rem' }}>{description}</div> : null}
        </div>
    )
}
